import { useState, type FormEvent } from 'react';
import { Plus } from 'lucide-react';
import type { AlertRule, AlertRuleInput, AlertRuleKind, AlertSeverity, ThresholdMetric, ThresholdOperator } from '@iot-dashboard/shared';
import { KIND_LABELS } from '@iot-dashboard/shared';
import { useDeleteRule, useDevices, useRules, useSaveRule, useSites } from '../../api/hooks';
import { DataTable, type Column } from '../../components/DataTable';
import { FormRow, Toggle } from '../../components/Form';
import { ConfirmDialog, Modal } from '../../components/Modal';
import { Panel } from '../../components/Panel';
import { SeverityBadge } from '../../components/SeverityBadge';
import { ErrorState, Loading, errorMessage } from '../../components/States';
import { useToast } from '../../components/Toast';
import { PageHeader } from '../../layout/PageHeader';
import { useTenantContext } from '../../layout/TenantLayout';

const KIND_LABEL: Record<AlertRuleKind, string> = {
  threshold: 'Reading threshold',
  leak: 'Water leak detected',
  'battery-low': 'Battery low',
  offline: 'Sensor offline',
  'door-open': 'Door / window left open',
  motion: 'Motion detected',
  tamper: 'Tampering detected',
};
const METRIC_LABEL: Record<ThresholdMetric, string> = { temperature: 'Temperature (°C)', humidity: 'Humidity (%)', light: 'Light (lux)', batteryPercentage: 'Battery (%)' };
const OP_LABEL: Record<ThresholdOperator, string> = { gt: 'above', gte: 'at or above', lt: 'below', lte: 'at or below' };

export function describeRule(r: AlertRule): string {
  switch (r.kind) {
    case 'threshold':
      return `${METRIC_LABEL[r.metric ?? 'temperature']} ${OP_LABEL[r.operator ?? 'gt']} ${r.threshold ?? '?'}`;
    case 'battery-low':
      return `Battery at or below ${r.threshold ?? 20} %`;
    case 'offline':
      return `Offline for ${r.durationMinutes ?? 15} min`;
    case 'door-open':
      return `Open for ${r.durationMinutes ?? 10} min`;
    default:
      return KIND_LABEL[r.kind];
  }
}

const blank = (): AlertRuleInput => ({ name: '', kind: 'threshold', enabled: true, severity: 'warning', metric: 'temperature', operator: 'gt', threshold: 30, durationMinutes: 15, deviceIds: [], siteIds: [], notifyEmails: [] });

export function AlertProfilesPage() {
  const { tenantId, canConfigure } = useTenantContext();
  const rules = useRules(tenantId);
  const devices = useDevices(tenantId);
  const sites = useSites(tenantId);
  const save = useSaveRule(tenantId);
  const remove = useDeleteRule(tenantId);
  const toast = useToast();
  const [editing, setEditing] = useState<{ ruleId?: string; builtIn: boolean; input: AlertRuleInput } | null>(null);
  const [deleting, setDeleting] = useState<AlertRule | null>(null);
  const [error, setError] = useState<string | null>(null);

  const scope = (r: AlertRule) => {
    const parts: string[] = [];
    if (r.deviceIds.length) parts.push(`${r.deviceIds.length} sensor${r.deviceIds.length === 1 ? '' : 's'}`);
    if (r.siteIds.length) parts.push(`${r.siteIds.length} site${r.siteIds.length === 1 ? '' : 's'}`);
    return parts.length ? parts.join(', ') : 'All sensors';
  };

  const toggle = async (r: AlertRule, enabled: boolean) => {
    try {
      await save.mutateAsync({ ruleId: r.ruleId, input: { ...r, enabled } });
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    setError(null);
    try {
      await save.mutateAsync({ ruleId: editing.ruleId, input: editing.input });
      toast.push(editing.ruleId ? 'Alert profile updated' : 'Alert profile created', 'success');
      setEditing(null);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    try {
      await remove.mutateAsync(deleting.ruleId);
      toast.push('Alert profile deleted', 'success');
      setDeleting(null);
    } catch (err) {
      toast.push(errorMessage(err), 'error');
    }
  };

  const columns: Column<AlertRule>[] = [
    { key: 'enabled', header: 'Enabled', render: (r) => <Toggle checked={r.enabled} disabled={!canConfigure || save.isPending} onChange={(v) => toggle(r, v)} /> },
    { key: 'name', header: 'Profile', primary: true, sortValue: (r) => r.name, render: (r) => <div><div className="cell-primary">{r.name}</div><div className="cell-sub">{r.builtIn ? 'Built-in' : 'Custom'}</div></div> },
    { key: 'condition', header: 'Condition', render: (r) => describeRule(r) },
    { key: 'severity', header: 'Severity', sortValue: (r) => r.severity, render: (r) => <SeverityBadge severity={r.severity} /> },
    { key: 'scope', header: 'Applies to', render: (r) => scope(r) },
    { key: 'notify', header: 'Notify', render: (r) => r.notifyEmails.length ? r.notifyEmails.join(', ') : <span className="muted">Dashboard only</span> },
    ...(canConfigure ? [{ key: 'actions', header: '', render: (r: AlertRule) => (
      <span className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn--sm" onClick={() => setEditing({ ruleId: r.ruleId, builtIn: r.builtIn, input: { name: r.name, kind: r.kind, enabled: r.enabled, severity: r.severity, metric: r.metric ?? 'temperature', operator: r.operator ?? 'gt', threshold: r.threshold ?? (r.kind === 'battery-low' ? 20 : 30), durationMinutes: r.durationMinutes ?? 15, deviceIds: r.deviceIds, siteIds: r.siteIds, notifyEmails: r.notifyEmails } })}>Edit</button>
        {!r.builtIn ? <button type="button" className="btn btn--sm btn--danger" onClick={() => setDeleting(r)}>Delete</button> : null}
      </span>
    ) } satisfies Column<AlertRule>] : []),
  ];

  const input = editing?.input;
  const set = <K extends keyof AlertRuleInput>(k: K, v: AlertRuleInput[K]) => setEditing((e) => (e ? { ...e, input: { ...e.input, [k]: v } } : e));

  return (
    <>
      <PageHeader
        title="Alert profiles"
        subtitle="Conditions that open an alert. Built-in profiles cover leaks, low batteries, offline sensors and tampering; add thresholds for temperature, humidity or light."
        actions={canConfigure ? <button type="button" className="btn btn--primary" onClick={() => setEditing({ builtIn: false, input: blank() })}><Plus size={16} /> New profile</button> : null}
      />
      <Panel flush>
        {rules.isPending ? <Loading /> : rules.isError ? <ErrorState error={rules.error} retry={() => rules.refetch()} /> : (
          <DataTable columns={columns} rows={rules.data} rowKey={(r) => r.ruleId} refetching={rules.isFetching} />
        )}
      </Panel>

      {editing && input ? (
        <Modal title={editing.ruleId ? 'Edit alert profile' : 'New alert profile'} onClose={() => setEditing(null)} width={640}>
          <form className="form" onSubmit={submit}>
            {error ? <div className="form-error">{error}</div> : null}
            <FormRow label="Name" required><input className="input" required minLength={2} maxLength={80} value={input.name} onChange={(e) => set('name', e.target.value)} /></FormRow>
            <FormRow label="Condition">
              <select className="select" value={input.kind} disabled={editing.builtIn} onChange={(e) => set('kind', e.target.value as AlertRuleKind)}>
                {(Object.keys(KIND_LABEL) as AlertRuleKind[]).map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
              </select>
            </FormRow>
            {input.kind === 'threshold' ? (
              <FormRow label="Threshold">
                <div className="row" style={{ flexWrap: 'nowrap' }}>
                  <select className="select" value={input.metric} onChange={(e) => set('metric', e.target.value as ThresholdMetric)}>
                    {(Object.keys(METRIC_LABEL) as ThresholdMetric[]).map((m) => <option key={m} value={m}>{METRIC_LABEL[m]}</option>)}
                  </select>
                  <select className="select" value={input.operator} onChange={(e) => set('operator', e.target.value as ThresholdOperator)}>
                    {(Object.keys(OP_LABEL) as ThresholdOperator[]).map((o) => <option key={o} value={o}>{OP_LABEL[o]}</option>)}
                  </select>
                  <input className="input" type="number" step="0.1" required value={input.threshold ?? ''} onChange={(e) => set('threshold', Number(e.target.value))} />
                </div>
              </FormRow>
            ) : null}
            {input.kind === 'battery-low' ? (
              <FormRow label="Battery at or below (%)"><input className="input" type="number" min={1} max={99} value={input.threshold ?? 20} onChange={(e) => set('threshold', Number(e.target.value))} /></FormRow>
            ) : null}
            {(input.kind === 'offline' || input.kind === 'door-open') ? (
              <FormRow label="Duration (minutes)" hint={input.kind === 'door-open' ? '0 alerts as soon as the door opens.' : 'How long a sensor must be unreachable before alerting.'}>
                <input className="input" type="number" min={0} max={10080} value={input.durationMinutes ?? 15} onChange={(e) => set('durationMinutes', Number(e.target.value))} />
              </FormRow>
            ) : null}
            <FormRow label="Severity">
              <select className="select" value={input.severity} onChange={(e) => set('severity', e.target.value as AlertSeverity)}>
                <option value="critical">Critical</option><option value="warning">Warning</option><option value="info">Info</option>
              </select>
            </FormRow>
            <FormRow label="Sensors" hint="Leave empty to apply to every sensor.">
              <div className="checklist" style={{ maxHeight: 180, overflow: 'auto' }}>
                {(devices.data ?? []).map((d) => (
                  <label key={d.deviceId}>
                    <input type="checkbox" checked={input.deviceIds.includes(d.deviceId)} onChange={(e) => set('deviceIds', e.target.checked ? [...input.deviceIds, d.deviceId] : input.deviceIds.filter((x) => x !== d.deviceId))} />
                    {d.name} <span className="muted small">{KIND_LABELS[d.kind]}</span>
                  </label>
                ))}
                {devices.data?.length === 0 ? <span className="muted">No sensors synced yet.</span> : null}
              </div>
            </FormRow>
            <FormRow label="Sites" hint="Leave empty to apply to every site.">
              <div className="checklist">
                {(sites.data ?? []).map((s) => (
                  <label key={s.siteId}>
                    <input type="checkbox" checked={input.siteIds.includes(s.siteId)} onChange={(e) => set('siteIds', e.target.checked ? [...input.siteIds, s.siteId] : input.siteIds.filter((x) => x !== s.siteId))} />
                    {s.name}
                  </label>
                ))}
              </div>
            </FormRow>
            <FormRow label="Notify (emails)" hint="Comma separated. Recorded on the alert; email delivery is configured by the MSP.">
              <input className="input" value={input.notifyEmails.join(', ')} onChange={(e) => set('notifyEmails', e.target.value.split(',').map((s) => s.trim()).filter(Boolean))} />
            </FormRow>
            <FormRow label="Enabled"><Toggle checked={input.enabled} onChange={(v) => set('enabled', v)} /></FormRow>
            <div className="form-actions">
              <button type="button" className="btn" onClick={() => setEditing(null)}>Cancel</button>
              <button type="submit" className="btn btn--primary" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save profile'}</button>
            </div>
          </form>
        </Modal>
      ) : null}

      {deleting ? (
        <ConfirmDialog title="Delete alert profile" message={<>Delete <strong>{deleting.name}</strong>? Open alerts from this profile will be resolved on the next sync.</>} confirmLabel="Delete" danger busy={remove.isPending} onConfirm={confirmDelete} onClose={() => setDeleting(null)} />
      ) : null}
    </>
  );
}
